import { forwardRef, useImperativeHandle } from 'react';

import { Box, Button, Flex, Grid, Icon, Text, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuPlus, LuTrash2 } from 'react-icons/lu';
import { z } from 'zod';

import { Form, FormField } from '@/components';

import { useAccountData } from './AccountDataContext';

// Updated schema to align with API CertificateRequest structure
const certificateItemSchema = z.object({
  certificateName: z
    .string()
    .min(1, t('common:account.validation.certificateNameRequired')),
  issuingOrganization: z.string().optional(),
  issueDate: z.date().optional(),
  explanation: z.string().optional(),
});

const certificateSchema = z.object({
  certificates: z.array(certificateItemSchema),
});

export type CertificateInfoFormFields = z.infer<typeof certificateSchema>;
export type CertificateItemFormFields = z.infer<typeof certificateItemSchema>;

export interface CertificateInfoFormRef {
  submit: () => void;
}

export const CertificateInfoForm = forwardRef<CertificateInfoFormRef>(
  (_props, ref) => {
    const { t } = useTranslation(['common', 'candidate', 'cv', 'components']);
    const { data, updateCertificate } = useAccountData();

    // Create proper default certificate item
    const createDefaultCertificate = (): CertificateItemFormFields => ({
      certificateName: '',
      issuingOrganization: '',
      issueDate: undefined,
      explanation: '',
    });

    // Transform API data to form format
    const getFormDefaultValues = (): CertificateInfoFormFields => {
      if (!data?.certificate?.length) {
        return {
          certificates: [createDefaultCertificate()],
        };
      }

      const transformedCertificates = data.certificate.map((cert) => ({
        certificateName: cert.certificateName ?? '',
        issuingOrganization: cert.issuingOrganization ?? '',
        issueDate: cert.issueDate ? new Date(cert.issueDate) : undefined,
        explanation: cert.explanation ?? '',
      }));

      return { certificates: transformedCertificates };
    };

    const form = useForm<CertificateInfoFormFields>({
      mode: 'onBlur',
      resolver: zodResolver(certificateSchema),
      values: getFormDefaultValues(),
    });

    const { fields, append, remove } = useFieldArray({
      control: form.control,
      name: 'certificates',
    });

    // Expose submit function to parent via ref
    useImperativeHandle(ref, () => ({
      submit: () => {
        form.handleSubmit(handleSubmit)();
      },
    }));

    const handleSubmit = async (formData: CertificateInfoFormFields) => {
      // Transform form data to API format - convert dates to strings
      const transformedCertificates = formData.certificates.map(
        (certificate) => ({
          ...certificate,
          issueDate: certificate.issueDate?.toISOString(),
        })
      );

      await updateCertificate(transformedCertificates);
    };

    return (
      <Form {...form} onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          {fields.map((field, idx) => (
            <Box
              key={field.id}
              borderBottom={idx === fields.length - 1 ? 'none' : '1px solid'}
              borderColor="gray.50"
              pb={4}
            >
              <Flex justifyContent="space-between" alignItems="center" mb={2}>
                <Text fontWeight="semibold" fontSize={16}>
                  {t('common:account.fields.certificate.name.label')}{' '}
                  {fields.length > 1 ? idx + 1 : ''}
                </Text>
                <Button
                  onClick={() => append(createDefaultCertificate())}
                  colorScheme="brand"
                  variant="link"
                  leftIcon={
                    <Icon as={LuPlus} fontSize={20} color="brand.500" />
                  }
                >
                  {t('common:actions.addNew')}
                </Button>
              </Flex>

              <Grid
                templateColumns={{ base: '1fr', md: '1fr 1fr' }}
                gap={4}
                mt={4}
              >
                <FormField
                  control={form.control}
                  name={`certificates.${idx}.certificateName`}
                  label={t('common:account.fields.certificate.name.label')}
                  placeholder={t(
                    'common:account.fields.certificate.name.placeholder'
                  )}
                  type="text"
                  size={{ base: 'sm', md: 'md' }}
                />
                <FormField
                  control={form.control}
                  name={`certificates.${idx}.issuingOrganization`}
                  label={t(
                    'common:account.fields.certificate.issuingOrganization.label'
                  )}
                  placeholder={t(
                    'common:account.fields.certificate.issuingOrganization.placeholder'
                  )}
                  type="text"
                  size={{ base: 'sm', md: 'md' }}
                />
                <FormField
                  control={form.control}
                  name={`certificates.${idx}.issueDate`}
                  label={t('common:account.fields.certificate.issueDate.label')}
                  type="date"
                  size={{ base: 'sm', md: 'md' }}
                />
                <FormField
                  control={form.control}
                  name={`certificates.${idx}.explanation`}
                  label={t(
                    'common:account.fields.certificate.description.label'
                  )}
                  placeholder={t(
                    'common:account.fields.certificate.description.placeholder'
                  )}
                  type="text"
                  size={{ base: 'sm', md: 'md' }}
                />
              </Grid>

              <Flex justify="space-between" mt={4} gap={2}>
                {fields.length > 1 ? (
                  <Button
                    variant="link"
                    color="red.500"
                    onClick={() => remove(idx)}
                    leftIcon={
                      <Icon as={LuTrash2} fontSize={20} color="red.500" />
                    }
                  >
                    {t('common:actions.delete')}
                  </Button>
                ) : (
                  <Box />
                )}
              </Flex>
            </Box>
          ))}
        </VStack>
      </Form>
    );
  }
);

CertificateInfoForm.displayName = 'CertificateInfoForm';
